import { useState } from 'react';
import api, { downloadFile } from '../api';

const DUPLICATE_MODES = [
  { value: 'skip', label: 'Skip Duplicates', hint: 'Leave existing matching records untouched (default)' },
  { value: 'update', label: 'Update Existing', hint: 'Overwrite matching records with the imported values' },
  { value: 'create_new', label: 'Import Anyway (as new)', hint: 'Add a new record even if a match is found — ignored for GST/TDS/Financials/ROC, which allow at most one record per key' },
];

function ConsolidatedImportModal({ open, onClose, companyId, onImported }) {
  const [file, setFile] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('skip');
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleDownloadTemplate = () => {
    downloadFile('/import/consolidated-template', 'Krosifin_All_Modules_Import_Template.xlsx').catch(() =>
      setError('Could not download template')
    );
  };

  const handleFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setResults(null);
    setError(null);
  };

  const handleImport = () => {
    if (!file || !companyId) return;
    setUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('company_id', companyId);
    fd.append('duplicate_mode', duplicateMode);
    api
      .post('/import/consolidated', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => {
        setResults(res.data);
        setFile(null);
        if (res.data.some((r) => r.success_count > 0 || r.updated_count > 0)) onImported();
      })
      .catch((err) => setError(err.response?.data?.detail || 'Import failed'))
      .finally(() => setUploading(false));
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setError(null);
    onClose();
  };

  return (
    <div className="ncf-modal-overlay" onClick={handleClose}>
      <div className="ncf-modal company-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="ncf-modal-title">Import All Modules</h3>

        <p className="dash-table-sub" style={{ marginBottom: 12 }}>
          One workbook, one tab per module — Client Master, DSC Register, ITR Filing Status, Audit
          Status, GST Returns, TDS-TCS, Financials, ROC-MCA. Download the template, fill in whichever
          tabs you need (leave the rest blank or delete them), then upload it here.
        </p>

        <button
          type="button"
          className="dash-btn cal-btn-grey"
          onClick={handleDownloadTemplate}
          style={{ display: 'inline-block', marginBottom: 16 }}
        >
          Download Template
        </button>

        <div className="field-group">
          <span className="field-label">Excel File (.xlsx)</span>
          <input type="file" accept=".xlsx,.xls" className="field-input" onChange={handleFile} />
        </div>

        <div className="field-group">
          <span className="field-label">If a matching record already exists (applies to every tab)</span>
          <div className="checkbox-list">
            {DUPLICATE_MODES.map((m) => (
              <label key={m.value} className="checkbox-item" title={m.hint}>
                <input
                  type="radio"
                  name="duplicate-mode-consolidated"
                  checked={duplicateMode === m.value}
                  onChange={() => setDuplicateMode(m.value)}
                />
                {m.label}
              </label>
            ))}
          </div>
        </div>

        {error && <p className="dash-error">{error}</p>}

        {results && (
          <div style={{ marginTop: 12 }}>
            {results.length === 0 && <p className="dash-empty">No recognized tabs found.</p>}
            {results.map((r) => (
              <div className="dash-card" key={r.module} style={{ marginBottom: 10, padding: 12 }}>
                <p style={{ margin: 0, fontWeight: 600 }}>{r.module_label}</p>
                <p style={{ margin: '4px 0 0' }}>
                  <strong>{r.success_count}</strong> imported, <strong>{r.updated_count}</strong> updated,{' '}
                  <strong>{r.duplicate_count}</strong> skipped as duplicate, <strong>{r.failure_count}</strong> failed.
                </p>
                {r.duplicates.length > 0 && (
                  <table className="dash-table" style={{ marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Skipped (duplicate)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.duplicates.map((d, idx) => (
                        <tr key={idx}>
                          <td>{d.row || '—'}</td>
                          <td>{d.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.errors.length > 0 && (
                  <table className="dash-table" style={{ marginTop: 8 }}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.errors.map((e, idx) => (
                        <tr key={idx}>
                          <td>{e.row || '—'}</td>
                          <td>{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="ncf-modal-actions">
          <button type="button" className="dash-btn cal-btn-grey" onClick={handleClose}>
            {results ? 'Close' : 'Cancel'}
          </button>
          <button
            type="button"
            className="dash-btn dash-btn-orange"
            disabled={!file || uploading}
            onClick={handleImport}
          >
            {uploading ? 'Importing...' : 'Import All'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsolidatedImportModal;
